import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Video, Search, Eye, StopCircle, Clock, Users as UsersIcon,
  ChevronLeft, ChevronRight, DollarSign, AlertTriangle, Play
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

export default function AdminStreamsPage() {
  const { token, hasPermission } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [streams, setStreams] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedStream, setSelectedStream] = useState(null);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');

  useEffect(() => {
    fetchStreams();
    fetchLiveStreams();
    const interval = setInterval(fetchLiveStreams, 30000);
    return () => clearInterval(interval);
  }, [pagination.page, statusFilter, search]);

  const fetchStreams = async () => {
    try {
      const res = await axios.get(`${API}/streams`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pagination.page, limit: 15, status: statusFilter, search }
      });
      setStreams(res.data.streams || []);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load streams');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveStreams = async () => {
    try {
      const res = await axios.get(`${API}/streams/live`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveStreams(res.data.streams || []);
    } catch (err) {
      console.error('Failed to fetch live streams:', err);
    }
  };

  const handleTerminate = async () => {
    if (!selectedStream) return;
    
    try {
      await axios.post(`${API}/streams/${selectedStream.stream_id}/terminate`, {
        reason: terminateReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stream terminated successfully');
      setShowTerminateModal(false);
      setSelectedStream(null);
      setTerminateReason('');
      fetchStreams();
      fetchLiveStreams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to terminate stream');
    }
  };

  const viewStreamDetails = async (streamId) => {
    try {
      const res = await axios.get(`${API}/streams/${streamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedStream(res.data.stream);
    } catch (err) {
      toast.error('Failed to load stream details');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
            <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
            Live
          </span>
        );
      case 'ended':
        return (
          <span className="px-2.5 py-1 bg-zinc-700 text-zinc-400 text-xs font-medium rounded-full">
            Ended
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-streams-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Streams</h1>
          <p className="text-zinc-500 text-sm mt-1">Monitor and manage live streams</p>
        </div>
      </div>

      {/* Live streams section */}
      {liveStreams.length > 0 && (
        <div className="bg-gradient-to-r from-red-500/10 to-purple-500/10 rounded-2xl p-6 border border-red-500/20">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <h2 className="text-white font-semibold" style={{ fontFamily: 'Manrope' }}>
              Live Now ({liveStreams.length})
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {liveStreams.map((stream) => (
              <div key={stream.stream_id} className="bg-zinc-900/70 rounded-xl p-4 border border-zinc-800 hover:border-red-500/30 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                      {stream.profile_picture ? (
                        <img 
                          src={`https://dev.naofan.app/avatars/${stream.profile_picture}`}
                          alt={stream.broadcaster_name}
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold">{stream.broadcaster_name?.charAt(0) || 'N'}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium truncate max-w-[150px]">{stream.title}</p>
                      <p className="text-zinc-500 text-xs">{stream.broadcaster_name || 'Anonymous'}</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-red-400 text-xs">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
                    LIVE
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-zinc-400">
                    <span className="flex items-center gap-1">
                      <UsersIcon className="w-4 h-4" /> {stream.viewer_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {stream.duration_minutes || 0}m
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => viewStreamDetails(stream.stream_id)}
                      className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-all"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {hasPermission('streams.terminate') && (
                      <button
                        onClick={() => {
                          setSelectedStream(stream);
                          setShowTerminateModal(true);
                        }}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                      >
                        <StopCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            placeholder="Search streams..."
            className="w-full pl-10 pr-4 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-indigo-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="px-4 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
        >
          <option value="all">All Status</option>
          <option value="live">Live</option>
          <option value="ended">Ended</option>
          <option value="waiting">Waiting</option>
        </select>
      </div>

      {/* Streams table */}
      <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Stream</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Broadcaster</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Status</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Viewers</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Revenue</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Created</th>
                <th className="text-right px-6 py-4 text-zinc-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="h-12 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : streams.length > 0 ? (
                streams.map((stream) => (
                  <tr key={stream.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                          <Video className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium truncate max-w-[200px]">{stream.title}</p>
                          <p className="text-zinc-500 text-xs font-mono">{stream.stream_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                          {stream.profile_picture ? (
                            <img 
                              src={`https://dev.naofan.app/avatars/${stream.profile_picture}`}
                              alt={stream.broadcaster_name}
                              className="w-8 h-8 object-cover"
                            />
                          ) : (
                            <span className="text-white text-xs font-bold">{stream.broadcaster_name?.charAt(0) || 'N'}</span>
                          )}
                        </div>
                        <span className="text-zinc-400 text-sm">{stream.broadcaster_name || 'Anonymous'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(stream.status)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-400">{stream.viewer_count || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-amber-400 font-medium">
                        {stream.total_earnings?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-500 text-sm">
                        {new Date(stream.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewStreamDetails(stream.stream_id)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-all"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {stream.status === 'live' && hasPermission('streams.terminate') && (
                          <button
                            onClick={() => {
                              setSelectedStream(stream);
                              setShowTerminateModal(true);
                            }}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                          >
                            <StopCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-zinc-500">
                    No streams found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm">
              Page {pagination.page} of {pagination.pages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Terminate confirmation modal */}
      {showTerminateModal && selectedStream && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-md w-full border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3 text-red-400">
                <AlertTriangle className="w-6 h-6" />
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  Terminate Stream
                </h3>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-zinc-400">
                Are you sure you want to terminate the stream "<span className="text-white">{selectedStream.title}</span>"?
                This will disconnect all viewers and end the broadcast immediately.
              </p>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Reason for termination</label>
                <input
                  type="text"
                  value={terminateReason}
                  onChange={(e) => setTerminateReason(e.target.value)}
                  placeholder="e.g., Policy violation, inappropriate content"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-red-500"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowTerminateModal(false);
                    setTerminateReason('');
                  }}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTerminate}
                  className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                >
                  <StopCircle className="w-4 h-4" />
                  Terminate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
